// uploadForm.js
import React, { useState, useRef, useCallback, useEffect } from "react";

// ─── DATOS DE MEJORAS ────────────────────────────────────────────────────────
// Para reemplazar imágenes: cambiá las URLs en beforeImg y afterImg por tus fotos.
const MEJORAS = [
  {
    id: "alineacion",
    titulo: "Alineación vertical",
    descripcion: "Corregimos la perspectiva y verticalidad de paredes, marcos y estructuras para que cada foto luzca prolija y profesional.",
    beforeImg: "https://picsum.photos/seed/before-alin/800/500",
    afterImg:  "https://picsum.photos/seed/after-alin/800/500",
  },
  {
    id: "color",
    titulo: "Corrección de color",
    descripcion: "Ajustamos tonos, saturación y contraste para que los colores reales de la propiedad se vean fieles y atractivos.",
    beforeImg: "https://picsum.photos/seed/before-color/800/500",
    afterImg:  "https://picsum.photos/seed/after-color2/800/500",
  },
  {
    id: "iluminacion",
    titulo: "Iluminación",
    descripcion: "Equilibramos zonas oscuras y sobreexpuestas para lograr una iluminación uniforme y natural en cada ambiente.",
    beforeImg: "https://picsum.photos/seed/before-ilum/800/500",
    afterImg:  "https://picsum.photos/seed/after-ilum2/800/500",
  },
  {
    id: "blancos",
    titulo: "Balance de blancos",
    descripcion: "Eliminamos dominantes de color (amarillos de tungsteno, azules de fluorescente) para que los blancos sean blancos de verdad.",
    beforeImg: "https://picsum.photos/seed/before-white/800/500",
    afterImg:  "https://picsum.photos/seed/after-white2/800/500",
  },
  {
    id: "cielo",
    titulo: "Reemplazo del cielo",
    descripcion: "Sustituimos cielos grises o quemados por cielos despejados y con profundidad que realzan la fachada de la propiedad.",
    beforeImg: "https://picsum.photos/seed/before-sky/800/500",
    afterImg:  "https://picsum.photos/seed/after-sky2/800/500",
  },
  {
    id: "ventanas",
    titulo: "Recuperación de ventanas",
    descripcion: "Recuperamos el detalle exterior visible a través de las ventanas, eliminando el efecto de quemado por exceso de luz.",
    beforeImg: "https://picsum.photos/seed/before-win/800/500",
    afterImg:  "https://picsum.photos/seed/after-win2/800/500",
  },
];

// ─── COMPONENTE SLIDER ANTES/DESPUÉS ─────────────────────────────────────────
function BeforeAfterSlider({ beforeImg, afterImg }) {
  const [position, setPosition] = useState(50); // porcentaje 0-100
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const getPosition = useCallback((clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return (x / rect.width) * 100;
  }, []);

  // Mouse
  const onMouseDown = () => { isDragging.current = true; };
  const onMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    setPosition(getPosition(e.clientX));
  }, [getPosition]);
  const onMouseUp = () => { isDragging.current = false; };

  // Touch
  const onTouchMove = useCallback((e) => {
    setPosition(getPosition(e.touches[0].clientX));
  }, [getPosition]);

  useEffect(() => {
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
    return () => {
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [onMouseMove]);

  return (
    <div
      ref={containerRef}
      onMouseDown={onMouseDown}
      onTouchMove={onTouchMove}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16/9",
        overflow: "hidden",
        borderRadius: 10,
        cursor: "col-resize",
        userSelect: "none",
        touchAction: "none",
        background: "#0a1628",
      }}
    >
      {/* IMAGEN DESPUÉS (base, cubre todo) */}
      <img
        src={afterImg}
        alt="Después"
        draggable={false}
        style={{
          position: "absolute", inset: 0,
          width: "100%", height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />

      {/* IMAGEN ANTES (recortada a la izquierda del handle) */}
      <div
        style={{
          position: "absolute", inset: 0,
          width: `${position}%`,
          overflow: "hidden",
        }}
      >
        <img
          src={beforeImg}
          alt="Antes"
          draggable={false}
          style={{
            position: "absolute", inset: 0,
            width: containerRef.current
              ? `${containerRef.current.getBoundingClientRect().width}px`
              : "100%",
            maxWidth: "none",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* LÍNEA DIVISORIA */}
      <div
        style={{
          position: "absolute",
          top: 0, bottom: 0,
          left: `${position}%`,
          width: 2,
          background: "#fff",
          transform: "translateX(-50%)",
          pointerEvents: "none",
        }}
      />

      {/* HANDLE CENTRAL */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: `${position}%`,
          transform: "translate(-50%, -50%)",
          width: 38, height: 38,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 2px 12px rgba(0,0,0,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 4,
          pointerEvents: "none",
        }}
      >
        {/* Flechas izq/der */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M5 8L2 8M2 8L4 6M2 8L4 10" stroke="#1a7fd4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11 8L14 8M14 8L12 6M14 8L12 10" stroke="#1a7fd4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* ETIQUETA ANTES */}
      <div style={{
        position: "absolute", top: 10, left: 10,
        background: "rgba(0,0,0,0.55)",
        color: "#fff",
        fontSize: 11, fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        padding: "3px 8px",
        borderRadius: 4,
        pointerEvents: "none",
        opacity: position > 15 ? 1 : 0,
        transition: "opacity 0.2s",
      }}>Antes</div>

      {/* ETIQUETA DESPUÉS */}
      <div style={{
        position: "absolute", top: 10, right: 10,
        background: "rgba(26,127,212,0.75)",
        color: "#fff",
        fontSize: 11, fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        padding: "3px 8px",
        borderRadius: 4,
        pointerEvents: "none",
        opacity: position < 85 ? 1 : 0,
        transition: "opacity 0.2s",
      }}>Después</div>
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
function UploadForm() {
  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      minHeight: "100vh",
      background: "#f4f8fd",
      color: "#1a2740",
    }}>

      {/* HERO */}
      <div style={{ background: "#fff", padding: "3rem 2.5rem 2.5rem", borderBottom: "0.5px solid rgba(26,127,212,0.15)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "3.5rem" }}>
          <img src="/Nombre y Logo.png" alt="RealtyShot" style={{ height: 48, objectFit: "contain" }} />
        </div>
        <p style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "#1a7fd4", marginBottom: "1.25rem" }}>
          Edición profesional de imágenes
        </p>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 400, lineHeight: 1.2, maxWidth: 520, marginBottom: "1.5rem" }}>
          Tus fotos de propiedad, <em style={{ fontStyle: "italic", color: "#1a7fd4" }}>transformadas</em>
        </h1>
        <p style={{ fontSize: 15, fontWeight: 300, lineHeight: 1.75, color: "#4a5568", maxWidth: 480 }}>
          RealtyShot transforma las fotos de tus propiedades en imágenes de calidad profesional, sin necesidad de contratar un fotógrafo. Sacá las fotos con tu celular, subílas a la app y en 24 horas hábiles las tenés editadas y listas para publicar en los portales inmobiliarios.
        </p>
      </div>

      {/* CÓMO FUNCIONA */}
      <div style={{ background: "#fff", padding: "2.5rem", marginTop: 1 }}>
        <p style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "#6b7a8d", marginBottom: "1.75rem" }}>
          ¿Cómo funciona?
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.5rem" }}>
          {[
            { n: "01", title: "Subís tus fotos", desc: "Directamente desde la app, con tu celular." },
            { n: "02", title: "Editamos con estándares pro", desc: "Nuestro equipo trabaja con calidad profesional." },
            { n: "03", title: "Descargás en 48 hs", desc: "Desde tu dashboard, listas para publicar." },
          ].map(({ n, title, desc }) => (
            <div key={n} style={{ padding: "1.25rem", background: "#e8f4fc", borderRadius: 10, border: "0.5px solid rgba(26,127,212,0.15)" }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.75rem", fontWeight: 400, color: "rgba(26,127,212,0.25)", marginBottom: "0.75rem" }}>{n}</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#1a2740", marginBottom: "0.4rem" }}>{title}</div>
              <div style={{ fontSize: 13, fontWeight: 300, color: "#6b7a8d", lineHeight: 1.6 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* MÉTODO AVE3 */}
      <div style={{ background: "#0a1628", color: "#fff", padding: "2.5rem", marginTop: 1 }}>
        <p style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "#1a7fd4", marginBottom: "1rem" }}>
          El método AVE3
        </p>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", fontWeight: 400, marginBottom: "1rem" }}>
          Fotos que realmente impactan
        </h2>
        <p style={{ fontSize: 14, fontWeight: 300, lineHeight: 1.75, color: "rgba(255,255,255,0.65)", maxWidth: 480 }}>
          RealtyShot incluye una guía rápida con 4 tips simples para que tus fotos lleguen en condiciones y la edición final realmente impacte. No hace falta ser fotógrafo, simplemente tenelos presentes mientras capturás.
        </p>
      </div>

      {/* ── NUESTRAS MEJORAS ── */}
      <div style={{ background: "#f4f8fd", padding: "2.5rem", marginTop: 1 }}>
        <p style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "#6b7a8d", marginBottom: "0.5rem" }}>
          Lo que hacemos
        </p>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.75rem", fontWeight: 400, color: "#1a2740", marginBottom: "0.5rem" }}>
          Nuestras mejoras
        </h2>
        <p style={{ fontSize: 14, fontWeight: 300, color: "#4a5568", marginBottom: "2.5rem", maxWidth: 480 }}>
          Deslizá cada imagen para ver la diferencia entre el original y el resultado final.
        </p>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "2rem",
        }}>
          {MEJORAS.map((mejora) => (
            <div
              key={mejora.id}
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "0.5px solid rgba(26,127,212,0.15)",
                overflow: "hidden",
                boxShadow: "0 2px 16px rgba(26,39,64,0.06)",
              }}
            >
              {/* Slider */}
              <BeforeAfterSlider
                beforeImg={mejora.beforeImg}
                afterImg={mejora.afterImg}
              />

              {/* Texto */}
              <div style={{ padding: "1.25rem" }}>
                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.1rem",
                  fontWeight: 400,
                  color: "#1a2740",
                  marginBottom: "0.5rem",
                }}>
                  {mejora.titulo}
                </h3>
                <p style={{
                  fontSize: 13,
                  fontWeight: 300,
                  color: "#6b7a8d",
                  lineHeight: 1.65,
                  margin: 0,
                }}>
                  {mejora.descripcion}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PARA QUIÉN */}
      <div style={{ background: "#fff", padding: "2.5rem", marginTop: 1 }}>
        <p style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "#6b7a8d", marginBottom: "1rem" }}>
          ¿Para quién es RealtyShot?
        </p>
        <p style={{ fontSize: 15, fontWeight: 300, lineHeight: 1.75, color: "#4a5568", maxWidth: 480, marginBottom: "1.25rem" }}>
          Para agentes inmobiliarios que quieren publicar mejor sin depender de la agenda de un fotógrafo, sin costos fijos y sin complicaciones.
        </p>
        <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontStyle: "italic", color: "#1a7fd4" }}>
          Más velocidad. Más flexibilidad. Resultados profesionales.
        </p>
      </div>

      {/* FOOTER */}
      <div style={{ background: "#fff", borderTop: "0.5px solid rgba(26,127,212,0.15)", padding: "2rem 2.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginTop: 1 }}>
        <div>
          <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 400 }}>RealtyShot</p>
          <p style={{ fontSize: 12, fontWeight: 300, color: "rgba(107,122,141,0.6)", marginTop: 4 }}>© 2025</p>
        </div>
        <a href="mailto:info@realty-shot.com" style={{ fontSize: 13, fontWeight: 300, color: "#1a7fd4", textDecoration: "none", borderBottom: "0.5px solid #1a7fd4", paddingBottom: 1 }}>
          info@realty-shot.com
        </a>
      </div>

    </div>
  );
}

export default UploadForm;